import { CustomEventType } from "@/types/customEvent";
import { useCallback, useEffect, useState } from "react";

export const useCatchCustomEvent = <T extends keyof CustomEventType>(
  eventType: T,
  callback: (data: CustomEventType[T]) => void,
): CustomEventType[T] | null => {
  const memoizedCallback = useCallback(callback, [callback]);
  const [eventData, setEventData] = useState<CustomEventType[T] | null>(null);
  const handleEvent = (event: Event) => {
    const customEvent = event as CustomEvent<CustomEventType[T]>;
    memoizedCallback(customEvent.detail);
    setEventData(customEvent.detail);
  };

  useEffect(() => {
    window.addEventListener(eventType, handleEvent);

    return () => {
      window.removeEventListener(eventType, handleEvent);
    };
  }, [eventType, memoizedCallback]);

  // returning the eventData to be used in the component
  return eventData;
};

export const emitCustomEvent = <T extends keyof CustomEventType>(
  eventType: T,
  data?: CustomEventType[T],
): void => {
  const event = new CustomEvent<CustomEventType[T]>(eventType, {
    detail: data,
  });
  window.dispatchEvent(event);
};
